// Vendas.js - Atualizado 2026-01-18 - Modal completo
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { salesService, usersService, operadorasService } from '../services/api';
import { formatDate, formatDateOnly, formatDateTime, maskCPF, maskCNPJ, maskPhone, maskCEP, maskMoney, unmaskMoney, parseDateDDMMYYYY, maskDateDDMMYYYY, maskNameForList, maskDocumentForList, maskPhoneForList, maskEmailForList, maskAddressLineForList } from '../utils/masks';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Vendas.css';

// Data de hoje em Brasília no formato YYYY-MM-DD (para data_instalacao)
const getTodayBrasilia = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
};

// Mês/ano atuais em Brasília (America/Sao_Paulo)
const getCurrentMonthYearBrasilia = () => {
  const now = new Date();
  const brtMs = now.getTime() - 3 * 60 * 60 * 1000;
  const d = new Date(brtMs);
  return { mes: d.getUTCMonth() + 1, ano: d.getUTCFullYear() };
};

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const ORIGEM_VISUALIZACAO_LABEL = {
  vendas: 'Página Vendas (detalhes)',
  fila_tratamento: 'Fila — Modal Tratar venda',
  fila_historico: 'Fila — Modal Detalhes',
  vendas_editar: 'Página Vendas (editar)'
};

const Vendas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendas, setVendas] = useState([]);
  const [filteredVendas, setFilteredVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [editingVenda, setEditingVenda] = useState(null);
  const [changingStatus, setChangingStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newDataInstalacao, setNewDataInstalacao] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [changingAtribuicao, setChangingAtribuicao] = useState(null);
  const [novoTratadoPor, setNovoTratadoPor] = useState('');
  const [backoffices, setBackoffices] = useState([]);
  const [changingVendedor, setChangingVendedor] = useState(null);
  const [novoVendedorId, setNovoVendedorId] = useState('');
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Estatísticas
  const [stats, setStats] = useState({
    total: 0,
    pendentesBko: 0,
    pendentesVendedor: 0,
    pendentesSistemica: 0,
    pendentesBiometria: 0,
    pendentesAuditoria: 0,
    pendentesImput: 0,
    aguardandoInstalacao: 0,
    concluidas: 0,
    canceladas: 0
  });
  
  const currentMonthBrasilia = useMemo(getCurrentMonthYearBrasilia, []);
  // Filtros: por padrão "mês atual" (Brasília); mes/ano vazios = "Todos os meses"
  const [filtros, setFiltros] = useState({
    status: '',
    busca: '',
    vendedor_id: '',
    operadora_id: '',
    data_inicio: '',
    data_fim: '',
    data_instalacao_inicio: '',
    data_instalacao_fim: '',
    mes: currentMonthBrasilia.mes,
    ano: currentMonthBrasilia.ano
  });
  const [operadoras, setOperadoras] = useState([]);
  // Exibição dos filtros de data (dd/mm/yyyy) — evita bug de 1 dia a menos no celular com type="date"
  const [dataInicioDisplay, setDataInicioDisplay] = useState('');
  const [dataFimDisplay, setDataFimDisplay] = useState('');
  const [dataInstalacaoInicioDisplay, setDataInstalacaoInicioDisplay] = useState('');
  const [dataInstalacaoFimDisplay, setDataInstalacaoFimDisplay] = useState('');

  // Modal Exportar Vendas: período (hoje, semana, mês, personalizado)
  const [showModalExportar, setShowModalExportar] = useState(false);
  const [periodoExportar, setPeriodoExportar] = useState('hoje');
  const [dataInicioExportar, setDataInicioExportar] = useState('');
  const [dataFimExportar, setDataFimExportar] = useState('');
  const [histVizOpen, setHistVizOpen] = useState(false);
  const [histVizLoading, setHistVizLoading] = useState(false);
  const [histVizItems, setHistVizItems] = useState([]);

  useEffect(() => {
    loadVendedores();
    loadOperadoras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadVendas();
    loadStats(); // Recarregar estatísticas também quando filtros mudarem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filtros.status, filtros.vendedor_id, filtros.operadora_id, filtros.data_inicio, filtros.data_fim, filtros.data_instalacao_inicio, filtros.data_instalacao_fim, filtros.mes, filtros.ano]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendas, filtros.busca]);

  const loadVendedores = async () => {
    try {
      const users = await usersService.getAll();
      // Filtrar apenas vendedores e também incluir admin/diretor se necessário
      const vendedoresList = users.filter(u => 
        u.role === 'vendedor' || u.role === 'admin' || u.role === 'diretor' || u.role === 'supervisor' || u.role === 'coordenador'
      );
      setVendedores(vendedoresList);
      
      // Carregar backoffices para atribuição de BKO
      const backofficesList = users.filter(u => u.role === 'backoffice' && u.ativo);
      setBackoffices(backofficesList);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  const loadOperadoras = async () => {
    try {
      const data = await operadorasService.getAll();
      setOperadoras(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
    }
  };

  const loadVendas = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtros.status && filtros.status.trim() !== '') {
        filters.status = filtros.status;
      }
      if (filtros.vendedor_id && filtros.vendedor_id.trim() !== '' && ['admin', 'diretor', 'supervisor', 'supervisor_bko', 'coordenador'].includes(user?.role)) {
        filters.vendedor_id = filtros.vendedor_id;
      }
      if (filtros.operadora_id && filtros.operadora_id.trim() !== '') {
        filters.operadora_id = filtros.operadora_id;
      }
      // Intervalo de datas explícito tem prioridade; senão usar mes/ano (vendas do mês em Brasília)
      if (filtros.data_inicio && filtros.data_fim && filtros.data_inicio.trim() !== '' && filtros.data_fim.trim() !== '') {
        filters.data_inicio = filtros.data_inicio;
        filters.data_fim = filtros.data_fim;
      } else if (filtros.mes && filtros.ano && filtros.mes >= 1 && filtros.mes <= 12 && filtros.ano >= 2000) {
        filters.mes = filtros.mes;
        filters.ano = filtros.ano;
      }
      if (filtros.data_instalacao_inicio && filtros.data_instalacao_fim && filtros.data_instalacao_inicio.trim() !== '' && filtros.data_instalacao_fim.trim() !== '') {
        filters.data_instalacao_inicio = filtros.data_instalacao_inicio;
        filters.data_instalacao_fim = filtros.data_instalacao_fim;
      }
      if (filtros.busca && filtros.busca.trim() !== '') {
        filters.busca = filtros.busca.trim();
      }

      const response = await salesService.getAll(filters, currentPage, itemsPerPage);
      
      if (response.data && response.pagination) {
        setVendas(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotal(response.pagination.total);
      } else {
        // Fallback para compatibilidade com resposta antiga
        setVendas(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
      alert('Erro ao carregar vendas');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const filters = {};
      if (filtros.vendedor_id && ['admin', 'diretor', 'supervisor', 'supervisor_bko', 'coordenador'].includes(user?.role)) {
        filters.vendedor_id = filtros.vendedor_id;
      }
      if (filtros.operadora_id && filtros.operadora_id.trim() !== '') {
        filters.operadora_id = filtros.operadora_id;
      }
      if (filtros.data_inicio && filtros.data_fim && filtros.data_inicio.trim() !== '' && filtros.data_fim.trim() !== '') {
        filters.data_inicio = filtros.data_inicio;
        filters.data_fim = filtros.data_fim;
      } else if (filtros.mes && filtros.ano && filtros.mes >= 1 && filtros.mes <= 12 && filtros.ano >= 2000) {
        filters.mes = filtros.mes;
        filters.ano = filtros.ano;
      }
      if (filtros.data_instalacao_inicio && filtros.data_instalacao_fim && filtros.data_instalacao_inicio.trim() !== '' && filtros.data_instalacao_fim.trim() !== '') {
        filters.data_instalacao_inicio = filtros.data_instalacao_inicio;
        filters.data_instalacao_fim = filtros.data_instalacao_fim;
      }

      const response = await salesService.getAllForStats(filters);
      // O backend sempre retorna { data, pagination }
      const allVendas = Array.isArray(response) ? response : (response.data || []);
      
      setStats({
        total: allVendas.length,
        pendentesBko: allVendas.filter(v => v.status === 'pendente_bko').length,
        pendentesVendedor: allVendas.filter(v => v.status === 'pendente_vendedor').length,
        pendentesSistemica: allVendas.filter(v => v.status === 'pendente_sistemica').length,
        pendentesBiometria: allVendas.filter(v => v.status === 'pendente_biometria').length,
        pendentesAuditoria: allVendas.filter(v => v.status === 'pendente_auditoria').length,
        pendentesImput: allVendas.filter(v => v.status === 'pendente_imput').length,
        aguardandoInstalacao: allVendas.filter(v => v.status === 'aguardando_instalacao').length,
        concluidas: allVendas.filter(v => v.status === 'concluido').length,
        canceladas: allVendas.filter(v => v.status === 'cancelado').length
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const applyFilters = () => {
    // Busca (CPF/CNPJ/Nome) é feita no backend em todas as páginas; resultados já vêm filtrados em vendas
    setFilteredVendas([...vendas]);
  };

  const handleFilterChange = (name, value) => {
    if (['status', 'vendedor_id', 'operadora_id', 'data_inicio', 'data_fim', 'data_instalacao_inicio', 'data_instalacao_fim', 'mes', 'ano', 'busca'].includes(name)) {
      setCurrentPage(1);
    }
    setFiltros(prev => {
      const newFiltros = { ...prev, [name]: value };
      if (name === 'data_inicio' || name === 'data_fim') {
        newFiltros.mes = '';
        newFiltros.ano = '';
      }
      if (name === 'mes' || name === 'ano') {
        newFiltros.data_inicio = '';
        newFiltros.data_fim = '';
      }
      return newFiltros;
    });
  };

  const handleDataFiltroChange = (field, rawValue) => {
    setCurrentPage(1);
    const masked = maskDateDDMMYYYY(rawValue);
    if (field === 'data_inicio') {
      setDataInicioDisplay(masked);
    } else if (field === 'data_fim') {
      setDataFimDisplay(masked);
    } else if (field === 'data_instalacao_inicio') {
      setDataInstalacaoInicioDisplay(masked);
    } else {
      setDataInstalacaoFimDisplay(masked);
    }
    const iso = parseDateDDMMYYYY(masked);
    setFiltros(prev => ({ ...prev, [field]: iso || '', ...(field.startsWith('data_instalacao') ? {} : { mes: '', ano: '' }) }));
  };

  const handleApplyFilters = () => {
    loadVendas();
    applyFilters();
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    const current = getCurrentMonthYearBrasilia();
    setFiltros({
      status: '',
      busca: '',
      vendedor_id: '',
      operadora_id: '',
      data_inicio: '',
      data_fim: '',
      data_instalacao_inicio: '',
      data_instalacao_fim: '',
      mes: current.mes,
      ano: current.ano
    });
    setDataInicioDisplay('');
    setDataFimDisplay('');
    setDataInstalacaoInicioDisplay('');
    setDataInstalacaoFimDisplay('');
  };

  // Opções de mês para o select (últimos 24 meses + Todos os meses)
  const opcoesMesesVendas = useMemo(() => {
    const opts = [{ value: '', label: 'Todos os meses' }];
    const { mes: m0, ano: a0 } = getCurrentMonthYearBrasilia();
    for (let i = 0; i < 24; i++) {
      let mes = m0 - i;
      let ano = a0;
      while (mes <= 0) { mes += 12; ano -= 1; }
      opts.push({ value: `${mes}|${ano}`, label: `${MESES[mes - 1]} ${ano}` });
    }
    return opts;
  }, []);

  const handleMonthFilterChange = (value) => {
    setCurrentPage(1);
    if (!value) {
      setFiltros(prev => ({ ...prev, mes: '', ano: '', data_inicio: '', data_fim: '' }));
      setDataInicioDisplay('');
      setDataFimDisplay('');
    } else {
      const [m, a] = value.split('|').map(Number);
      setFiltros(prev => ({ ...prev, mes: m, ano: a, data_inicio: '', data_fim: '' }));
      setDataInicioDisplay('');
      setDataFimDisplay('');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Resetar para primeira página ao mudar itens por página
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'pendente_bko': 'status-pendente-bko',
      'tratando': 'status-tratando',
      'pendente_vendedor': 'status-pendente-vendedor',
      'pendente_sistemica': 'status-pendente-sistemica',
      'pendente_biometria': 'status-pendente-biometria',
      'pendente_auditoria': 'status-pendente-auditoria',
      'pendente_imput': 'status-pendente-imput',
      'aguardando_instalacao': 'status-aguardando-instalacao',
      'concluido': 'status-concluido',
      'cancelado': 'status-cancelado'
    };
    return statusMap[status] || 'status-pendente-bko';
  };

  const getStatusText = (status) => {
    const texts = {
      'pendente_bko': 'Pendente BKO',
      'pendente_vendedor': 'Pendente Vendedor',
      'pendente_sistemica': 'Pendente Sistêmica',
      'pendente_biometria': 'Pendente Biometria',
      'pendente_auditoria': 'Pendente Auditoria',
      'pendente_imput': 'Pendente Imput',
      'aguardando_instalacao': 'Aguardando Instalação',
      'concluido': 'Concluído',
      'cancelado': 'Cancelado'
    };
    return texts[status] || status;
  };

  const formatDocumento = (doc) => {
    if (!doc) return '';
    const clean = doc.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (clean.length === 10) {
      return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const parseObservacoesTratamento = (observacaoTratamento) => {
    if (!observacaoTratamento || !observacaoTratamento.trim()) {
      return [];
    }

    // Dividir por linhas e filtrar vazias
    const linhas = observacaoTratamento.split('\n').filter(linha => linha.trim());
    const observacoes = [];

    linhas.forEach(linha => {
      linha = linha.trim();
      if (!linha) return;

      // Padrão: [DD/MM/YYYY HH:MM] Status alterado de "X" para "Y" por Usuario
      const mudancaStatusMatch = linha.match(/^\[([^\]]+)\]\s*Status alterado de\s+"([^"]+)"\s+para\s+"([^"]+)"\s+por\s+(.+)$/);
      if (mudancaStatusMatch) {
        observacoes.push({
          tipo: 'mudanca_status',
          dataHora: mudancaStatusMatch[1],
          statusAnterior: mudancaStatusMatch[2],
          statusNovo: mudancaStatusMatch[3],
          usuario: mudancaStatusMatch[4],
          texto: linha
        });
        return;
      }

      // Padrão: [DD/MM/YYYY HH:MM] Usuario: observação
      const observacaoMatch = linha.match(/^\[([^\]]+)\]\s*(.+?):\s*(.+)$/);
      if (observacaoMatch) {
        observacoes.push({
          tipo: 'observacao',
          dataHora: observacaoMatch[1],
          usuario: observacaoMatch[2],
          texto: observacaoMatch[3],
          linhaCompleta: linha
        });
        return;
      }

      // Se não matchou nenhum padrão, adicionar como texto simples
      if (linha) {
        observacoes.push({
          tipo: 'texto',
          texto: linha
        });
      }
    });

    // Ordenar por data/hora (mais recente primeiro) se disponível
    return observacoes.reverse(); // Reverter para mostrar mais recente primeiro
  };

  const viewDetails = async (id) => {
    try {
      const venda = await salesService.getById(id);
      setSelectedVenda(venda);
      salesService.registrarVisualizacao(id, 'vendas').catch(() => {});
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      alert('Erro ao carregar detalhes da venda');
    }
  };

  const abrirHistVisualizacaoVendas = async () => {
    if (!selectedVenda?.id) return;
    setHistVizOpen(true);
    setHistVizLoading(true);
    setHistVizItems([]);
    try {
      const data = await salesService.getHistoricoVisualizacao(selectedVenda.id);
      setHistVizItems(Array.isArray(data) ? data : []);
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao carregar histórico de visualização');
      setHistVizItems([]);
    } finally {
      setHistVizLoading(false);
    }
  };

  const closeModal = () => {
    setHistVizOpen(false);
    setHistVizItems([]);
    setSelectedVenda(null);
    setEditingVenda(null);
    setChangingStatus(null);
    setNewStatus('');
    setNewDataInstalacao('');
    setChangingAtribuicao(null);
    setNovoTratadoPor('');
    setChangingVendedor(null);
    setNovoVendedorId('');
  };
  
  const openAtribuicaoBko = (venda) => {
    setChangingAtribuicao(venda);
    setNovoTratadoPor(venda.tratado_por || '');
  };

  const openAtribuicaoVendedor = (venda) => {
    setChangingVendedor(venda);
    setNovoVendedorId(venda.vendedor_id || '');
  };

  const handleAtribuicaoVendedorSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!novoVendedorId || novoVendedorId.toString().trim() === '') {
        alert('❌ Por favor, selecione um vendedor');
        return;
      }

      const vendedorId = parseInt(novoVendedorId);
      if (isNaN(vendedorId) || vendedorId <= 0) {
        alert('❌ ID de vendedor inválido');
        return;
      }

      console.log('Enviando atribuição de vendedor:', { 
        vendaId: changingVendedor.id, 
        novoVendedorId: vendedorId
      });
      
      await salesService.updateAtribuicaoVendedor(changingVendedor.id, vendedorId);
      
      alert('✅ Vendedor atualizado com sucesso!');
      await loadVendas();
      
      // Atualizar venda selecionada se for a mesma
      if (selectedVenda && selectedVenda.id === changingVendedor.id) {
        const vendaAtualizada = await salesService.getById(changingVendedor.id);
        setSelectedVenda(vendaAtualizada);
      }
      
      setChangingVendedor(null);
      setNovoVendedorId('');
    } catch (error) {
      console.error('Erro ao atualizar vendedor:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar vendedor';
      alert(`❌ ${errorMessage}`);
    }
  };
  
  const handleAtribuicaoBkoSubmit = async (e) => {
    e.preventDefault();
    try {
      // Se for string vazia ou não definido, enviar null explicitamente
      let tratadoPorId = null;
      
      // Verificar se novoTratadoPor tem valor válido
      // O select retorna string vazia '' quando "Limpar atribuição" é selecionado
      const valorTratado = novoTratadoPor;
      
      if (valorTratado && valorTratado.toString().trim() !== '' && valorTratado !== '0') {
        const parsedId = parseInt(valorTratado);
        if (!isNaN(parsedId) && parsedId > 0) {
          tratadoPorId = parsedId;
        }
      }
      // Se chegou até aqui sem atribuir valor, tratadoPorId continua null (limpar atribuição)
      
      console.log('Enviando atribuição de BKO:', { 
        vendaId: changingAtribuicao.id, 
        valorOriginal: valorTratado, 
        tratadoPorId,
        tipoOriginal: typeof valorTratado
      });
      
      await salesService.updateAtribuicaoBko(changingAtribuicao.id, tratadoPorId);
      
      alert('✅ Atribuição de BKO atualizada com sucesso!');
      await loadVendas();
      
      // Atualizar venda selecionada se for a mesma
      if (selectedVenda && selectedVenda.id === changingAtribuicao.id) {
        const vendaAtualizada = await salesService.getById(changingAtribuicao.id);
        setSelectedVenda(vendaAtualizada);
      }
      
      setChangingAtribuicao(null);
      setNovoTratadoPor('');
    } catch (error) {
      console.error('Erro ao atualizar atribuição de BKO:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar atribuição de BKO';
      alert(`❌ ${errorMessage}`);
    }
  };

  const editVenda = async (venda) => {
    try {
      const vendaCompleta = await salesService.getById(venda.id);
      setEditingVenda(vendaCompleta);
      salesService.registrarVisualizacao(venda.id, 'vendas_editar').catch(() => {});
    } catch (error) {
      console.error('Erro ao carregar venda para edição:', error);
      alert('Erro ao carregar venda para edição');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...editingVenda,
        documento: editingVenda.documento ? editingVenda.documento.replace(/\D/g, '') : '',
        telefone: editingVenda.telefone ? editingVenda.telefone.replace(/\D/g, '') : '',
        telefone2: editingVenda.telefone2 ? editingVenda.telefone2.replace(/\D/g, '') : null,
        cpf: editingVenda.cpf ? editingVenda.cpf.replace(/\D/g, '') : null,
        cep: editingVenda.cep ? editingVenda.cep.replace(/\D/g, '') : '',
        valor_total: typeof editingVenda.valor_total === 'string' 
          ? unmaskMoney(editingVenda.valor_total)
          : editingVenda.valor_total.toString().replace(/[^\d.]/g, ''),
        data_agendamento2: editingVenda.data_agendamento2 || null,
        periodo_agendamento2: editingVenda.periodo_agendamento2 || null,
        data_nascimento: editingVenda.data_nascimento || null,
        operadora_viabilidade_json: editingVenda.operadora_viabilidade_json ? 
          (typeof editingVenda.operadora_viabilidade_json === 'string' 
            ? editingVenda.operadora_viabilidade_json 
            : JSON.stringify(editingVenda.operadora_viabilidade_json)) 
          : null
      };

      await salesService.update(editingVenda.id, updateData);
      alert('✅ Venda atualizada com sucesso!');
      closeModal();
      loadVendas();
    } catch (error) {
      console.error('Erro ao atualizar venda:', error);
      alert('❌ Erro ao atualizar venda: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const openChangeStatus = async (venda) => {
    setChangingStatus(venda);
    setNewStatus(venda.status);
    const dataInicial = venda.data_instalacao || venda.data_agendamento1 || getTodayBrasilia();
    setNewDataInstalacao(venda.status === 'aguardando_instalacao' ? dataInicial : '');
  };

  const handleChangeStatus = async () => {
    if (!newStatus) {
      alert('Selecione um status');
      return;
    }
    if (newStatus === 'aguardando_instalacao' && !newDataInstalacao?.trim()) {
      alert('Informe a data da instalação para o status "Aguardando Instalação".');
      return;
    }

    try {
      const dataInstalacao = newStatus === 'aguardando_instalacao' ? newDataInstalacao : undefined;
      await salesService.updateStatus(changingStatus.id, newStatus, '', dataInstalacao);
      alert('✅ Status atualizado com sucesso!');
      closeModal();
      loadVendas();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('❌ Erro ao atualizar status');
    }
  };

  const handleDeleteVenda = async (venda) => {
    const clienteNome = venda.nome_cliente || venda.razao_social || `Venda #${venda.id}`;
    const confirmMessage = `Tem certeza que deseja excluir a venda "${clienteNome}"?\n\nEsta ação não pode ser desfeita!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await salesService.delete(venda.id);
      alert('✅ Venda excluída com sucesso!');
      loadVendas();
      loadStats();
    } catch (error) {
      console.error('Erro ao excluir venda:', error);
      alert('❌ Erro ao excluir venda: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleVoltarFila = async (venda) => {
    const clienteNome = venda.nome_cliente || venda.razao_social || `Venda #${venda.id}`;
    const confirmMessage = `Tem certeza que deseja voltar a venda "${clienteNome}" para a fila de tratamento?\n\nA venda voltará como "Pendente BKO" mantendo a atribuição atual.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Mudar status para pendente_bko mantendo a atribuição
      await salesService.updateStatus(venda.id, 'pendente_bko', '');
      alert('✅ Venda voltou para a fila de tratamento com sucesso!');
      
      // Recarregar vendas e estatísticas
      await loadVendas();
      loadStats();
      
      // Se a venda estava sendo visualizada, atualizar também
      if (selectedVenda && selectedVenda.id === venda.id) {
        const vendaAtualizada = await salesService.getById(venda.id);
        setSelectedVenda(vendaAtualizada);
      }
    } catch (error) {
      console.error('Erro ao voltar venda à fila:', error);
      alert('❌ Erro ao voltar venda à fila: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  // Retorna { data_inicio, data_fim } em YYYY-MM-DD conforme período (Brasília: semana = segunda a domingo, mês = dia 01 ao último)
  const getExportDateRange = () => {
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const now = new Date();

    if (periodoExportar === 'hoje') {
      const hoje = toYMD(now);
      return { data_inicio: hoje, data_fim: hoje };
    }
    if (periodoExportar === 'semana') {
      const day = now.getDay(); // 0=domingo, 1=segunda...
      const daysFromMonday = day === 0 ? 6 : day - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - daysFromMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { data_inicio: toYMD(monday), data_fim: toYMD(sunday) };
    }
    if (periodoExportar === 'mes') {
      const ano = now.getFullYear();
      const mes = now.getMonth();
      const primeiro = new Date(ano, mes, 1);
      const ultimo = new Date(ano, mes + 1, 0);
      return { data_inicio: toYMD(primeiro), data_fim: toYMD(ultimo) };
    }
    // personalizado
    return { data_inicio: dataInicioExportar, data_fim: dataFimExportar };
  };

  const handleConfirmarExportar = async () => {
    if (periodoExportar === 'personalizado') {
      if (!dataInicioExportar || !dataFimExportar) {
        alert('Preencha a data inicial e a data final para o período personalizado.');
        return;
      }
      if (dataInicioExportar > dataFimExportar) {
        alert('A data inicial não pode ser maior que a data final.');
        return;
      }
    }

    try {
      const { data_inicio, data_fim } = getExportDateRange();
      const filters = { data_inicio, data_fim };
      if (filtros.status) {
        filters.status = filtros.status;
      }
      if (filtros.vendedor_id && ['admin', 'diretor', 'supervisor', 'supervisor_bko', 'coordenador'].includes(user?.role)) {
        filters.vendedor_id = filtros.vendedor_id;
      }

      await salesService.exportSales(filters);
      setShowModalExportar(false);
      alert('✅ Vendas exportadas com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar vendas:', error);
      alert('❌ Erro ao exportar vendas: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  return (
    <Layout>
      <div className="vendas-page">
        {/* Header */}
        <div className="vendas-header">
          <div>
            <h1 className="card-title">📊 Vendas</h1>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              ← Voltar
            </button>
            {user && ['admin', 'diretor', 'supervisor', 'supervisor_bko', 'coordenador'].includes(user.role) && (
              <button onClick={() => setShowModalExportar(true)} className="btn btn-success">
                📥 Exportar Vendas
              </button>
            )}
            {user && user.role !== 'backoffice' && user.role !== 'supervisor_bko' && (
              <button onClick={() => navigate('/vendas/consulta-viabilidade')} className="btn btn-primary">
                ➕ Nova Venda
              </button>
            )}
          </div>
        </div>

        {/* Estatísticas */}
        <div className="vendas-stats">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total de Vendas</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.pendentesBko}</div>
            <div className="stat-label">Pendente BKO</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.pendentesVendedor}</div>
            <div className="stat-label">Pendente Vendedor</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.pendentesSistemica}</div>
            <div className="stat-label">Pendente Sistêmica</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.aguardandoInstalacao}</div>
            <div className="stat-label">Aguardando Instalação</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.concluidas}</div>
            <div className="stat-label">Concluídas</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.canceladas}</div>
            <div className="stat-label">Canceladas</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="filter-section">
          <h3 className="filter-section__title">🔍 Filtros</h3>

          <div className="filter-section__group">
            <span className="filter-section__group-label">Período</span>
            <div className="filter-grid filter-grid--period">
              <div className="filter-group">
                <label htmlFor="filtro-mes">Mês</label>
                <select
                  id="filtro-mes"
                  value={filtros.mes && filtros.ano ? `${filtros.mes}|${filtros.ano}` : ''}
                  onChange={(e) => handleMonthFilterChange(e.target.value)}
                >
                  {opcoesMesesVendas.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-data-inicio">Data Início</label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="filtro-data-inicio"
                  placeholder="dd/mm/aaaa"
                  maxLength={10}
                  value={dataInicioDisplay}
                  onChange={(e) => handleDataFiltroChange('data_inicio', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-data-fim">Data Fim</label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="filtro-data-fim"
                  placeholder="dd/mm/aaaa"
                  maxLength={10}
                  value={dataFimDisplay}
                  onChange={(e) => handleDataFiltroChange('data_fim', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-data-instalacao-inicio">Data Instalação Início</label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="filtro-data-instalacao-inicio"
                  placeholder="dd/mm/aaaa"
                  maxLength={10}
                  value={dataInstalacaoInicioDisplay}
                  onChange={(e) => handleDataFiltroChange('data_instalacao_inicio', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-data-instalacao-fim">Data Instalação Fim</label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="filtro-data-instalacao-fim"
                  placeholder="dd/mm/aaaa"
                  maxLength={10}
                  value={dataInstalacaoFimDisplay}
                  onChange={(e) => handleDataFiltroChange('data_instalacao_fim', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="filter-section__group">
            <span className="filter-section__group-label">Status e contexto</span>
            <div className="filter-grid filter-grid--main">
              <div className="filter-group">
                <label htmlFor="filtro-status">Status</label>
                <select
                  id="filtro-status"
                  value={filtros.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">Todos os status</option>
                  <option value="pendente_bko">Pendente BKO</option>
                  <option value="pendente_vendedor">Pendente Vendedor</option>
                  <option value="pendente_sistemica">Pendente Sistêmica</option>
                  <option value="pendente_biometria">Pendente Biometria</option>
                  <option value="pendente_auditoria">Pendente Auditoria</option>
                  <option value="pendente_imput">Pendente Imput</option>
                  <option value="aguardando_instalacao">Aguardando Instalação</option>
                  <option value="concluido">Concluído</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              {['admin', 'diretor', 'supervisor', 'supervisor_bko', 'coordenador'].includes(user?.role) && (
                <div className="filter-group">
                  <label htmlFor="filtro-vendedor">Vendedor</label>
                  <select
                    id="filtro-vendedor"
                    value={filtros.vendedor_id}
                    onChange={(e) => handleFilterChange('vendedor_id', e.target.value)}
                  >
                    <option value="">Todos os vendedores</option>
                    {vendedores.map(vendedor => (
                      <option key={vendedor.id} value={vendedor.id}>
                        {vendedor.nome || vendedor.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="filter-group">
                <label htmlFor="filtro-operadora">Operadora</label>
                <select
                  id="filtro-operadora"
                  value={filtros.operadora_id}
                  onChange={(e) => handleFilterChange('operadora_id', e.target.value)}
                >
                  <option value="">Todas as operadoras</option>
                  {operadoras.filter(op => op.ativo !== false).map(op => (
                    <option key={op.id} value={op.id}>
                      {op.nome_display || op.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="filter-section__group">
            <span className="filter-section__group-label">Busca</span>
            <div className="filter-grid filter-grid--search">
              <div className="filter-group">
                <label htmlFor="filtro-busca">CPF/CNPJ ou Nome</label>
                <input
                  type="text"
                  id="filtro-busca"
                  value={filtros.busca}
                  onChange={(e) => handleFilterChange('busca', e.target.value)}
                  placeholder="Digite CPF, CNPJ ou nome do cliente..."
                />
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button
              onClick={handleClearFilters}
              className="btn"
              style={{
                backgroundColor: 'var(--cinza-escuro-2)',
                color: 'var(--texto-primario)',
                padding: '0.5rem 1.25rem',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Limpar
            </button>
            <button
              onClick={handleApplyFilters}
              className="btn btn-primary"
              style={{
                padding: '0.5rem 1.25rem',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Aplicar filtros
            </button>
          </div>
        </div>

        {/* Tabela de Vendas */}
        {loading ? (
          <div className="loading">Carregando vendas...</div>
        ) : filteredVendas.length === 0 ? (
          <div className="no-vendas">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
            <h3>Nenhuma venda encontrada</h3>
            <p>Comece criando sua primeira venda</p>
            {user && user.role !== 'backoffice' && user.role !== 'supervisor_bko' && (
              <button
                onClick={() => navigate('/vendas/consulta-viabilidade')}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                ➕ Nova Venda
              </button>
            )}
          </div>
        ) : (
          <div className="vendas-table">
            <div className="table-header">
              <div>ID</div>
              <div>Cliente</div>
              <div>Documento</div>
              <div>Telefone</div>
              <div>Valor</div>
              <div>Status</div>
              <div>Vendedor</div>
              <div>Data Criação</div>
              <div>Ações</div>
            </div>
            
            {filteredVendas.map((venda) => (
              <div key={venda.id} className="table-row">
                <div>{venda.id}</div>
                <div>
                  {venda.tipo_cliente === 'cnpj' ? (
                    <>
                      <strong>{maskNameForList(venda.razao_social || venda.nome_cliente || 'N/A')}</strong>
                      {venda.nome_cliente && venda.nome_cliente !== venda.razao_social && (
                        <><br /><small style={{ color: '#888' }}>📋 {maskNameForList(venda.nome_cliente)}</small></>
                      )}
                    </>
                  ) : (
                    <strong>{maskNameForList(venda.nome_cliente || 'N/A')}</strong>
                  )}
                  <br />
                  <small style={{ color: '#666' }}>
                    {maskDocumentForList(venda.documento, venda.tipo_cliente)}
                    {venda.tipo_cliente === 'cnpj' ? (
                      <span className="badge-cnpj">CNPJ</span>
                    ) : (
                      <span className="badge-cpf">CPF</span>
                    )}
                  </small>
                  {venda.email && (
                    <><br /><small style={{ color: '#888' }}>📧 {maskEmailForList(venda.email)}</small></>
                  )}
                  {venda.telefone && (
                    <><br /><small style={{ color: '#888' }}>📱 {maskPhoneForList(venda.telefone)}</small></>
                  )}
                  {venda.logradouro && (
                    <><br /><small style={{ color: '#888' }}>
                      📍 {maskAddressLineForList(venda.logradouro, venda.numero, venda.bairro, venda.cidade, venda.estado)}
                    </small></>
                  )}
                </div>
                <div>{maskDocumentForList(venda.documento, venda.tipo_cliente)}</div>
                <div>{maskPhoneForList(venda.telefone)}</div>
                <div>
                  {(user?.role === 'backoffice' || user?.role === 'supervisor_bko') && venda.status === 'concluido'
                    ? '—'
                    : `R$ ${parseFloat(venda.valor_total || 0).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}`}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                  {venda.status === 'aguardando_instalacao' && (
                    <small style={{ color: 'var(--texto-secundario)', whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {(venda.data_instalacao || venda.data_agendamento1) ? formatDateOnly(venda.data_instalacao || venda.data_agendamento1) : '—'}
                    </small>
                  )}
                  <span className={`status-badge ${getStatusClass(venda.status)}`}>
                    {getStatusText(venda.status)}
                  </span>
                </div>
                <div>
                  <strong>{venda.vendedor?.nome || 'N/A'}</strong>
                </div>
                <div>
                  <strong>{formatDate(venda.created_at)}</strong>
                  <br />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    {new Date(venda.created_at).toLocaleTimeString('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
                <div className="action-buttons">
                  <button
                    onClick={() => viewDetails(venda.id)}
                    className="btn-small btn-view"
                  >
                    Ver
                  </button>
                  {(
                    // Admin e Diretor podem editar qualquer venda
                    ['admin', 'diretor'].includes(user?.role) ||
                    // Vendedor pode editar apenas vendas próprias com status pendente_vendedor
                    (user?.role === 'vendedor' && venda.status === 'pendente_vendedor' && venda.vendedor_id === user.id) ||
                    // Supervisor/Coordenador pode editar vendas pendente_vendedor da equipe
                    ((user?.role === 'supervisor' || user?.role === 'coordenador') && venda.status === 'pendente_vendedor')
                  ) && (
                    <button
                      onClick={() => editVenda(venda)}
                      className="btn-small btn-edit"
                    >
                      Editar
                    </button>
                  )}
                  {['admin', 'diretor'].includes(user?.role) && (
                    <button
                      onClick={() => openChangeStatus(venda)}
                      className="btn-small"
                      style={{ background: '#607d8b', color: '#fff' }}
                    >
                      Status
                    </button>
                  )}
                  {/* Botão Voltar à Fila - Backoffice quando atribuída a ele; Supervisor BKO nas vendas dos subordinados */}
                  {((user?.role === 'backoffice' && venda.tratado_por === user.id) ||
                    (user?.role === 'supervisor_bko' && venda.tratado_por)) && (
                    <button
                      onClick={() => handleVoltarFila(venda)}
                      className="btn-small"
                      style={{ background: '#ff9800', color: '#fff' }}
                      title="Voltar esta venda para a fila de tratamento (Pendente BKO)"
                    >
                      🔄 Voltar à Fila
                    </button>
                  )}
                  {['admin', 'diretor'].includes(user?.role) && (
                    <button
                      onClick={() => handleDeleteVenda(venda)}
                      className="btn-small"
                      style={{ background: '#d32f2f', color: '#fff' }}
                    >
                      🗑️ Excluir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Controles de Paginação */}
        {!loading && filteredVendas.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'var(--cinza-escuro-2)',
            borderRadius: '8px',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ color: 'var(--texto-primario)' }}>
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, total)} de {total} vendas
              </span>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--cinza-escuro-1)',
                  color: 'var(--texto-primario)',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === 1 ? 'var(--cinza-escuro-1)' : 'var(--azul-primario)',
                  color: currentPage === 1 ? '#666' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                ⏮️ Primeira
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === 1 ? 'var(--cinza-escuro-1)' : 'var(--azul-primario)',
                  color: currentPage === 1 ? '#666' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                ⬅️ Anterior
              </button>
              
              <span style={{ 
                padding: '0.5rem 1rem',
                color: 'var(--texto-primario)',
                fontWeight: 'bold'
              }}>
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === totalPages ? 'var(--cinza-escuro-1)' : 'var(--azul-primario)',
                  color: currentPage === totalPages ? '#666' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Próxima ➡️
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === totalPages ? 'var(--cinza-escuro-1)' : 'var(--azul-primario)',
                  color: currentPage === totalPages ? '#666' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Última ⏭️
              </button>
            </div>
          </div>
        )}

        {/* Modal de detalhes */}
        {selectedVenda && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-venda" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h2 style={{ margin: 0 }}>👁️ Detalhes da Venda #{selectedVenda.id}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {['admin', 'diretor'].includes(user?.role) && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                      onClick={abrirHistVisualizacaoVendas}
                    >
                      Hist. visualização
                    </button>
                  )}
                  <span className="close-modal" onClick={closeModal}>×</span>
                </div>
              </div>
              
              <div className="modal-body-venda">
                {/* Informações do Cliente */}
                <div className="info-section">
                  <h4>👤 Informações do Cliente</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Nome/Razão Social</div>
                      <div className="info-value">
                        {selectedVenda.tipo_cliente === 'cnpj' 
                          ? (selectedVenda.razao_social || selectedVenda.nome_cliente)
                          : selectedVenda.nome_cliente
                        }
                      </div>
                    </div>
                    {selectedVenda.tipo_cliente === 'cnpj' && selectedVenda.nome_cliente && (
                      <div className="info-item">
                        <div className="info-label">Nome do Cliente</div>
                        <div className="info-value">{selectedVenda.nome_cliente}</div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Tipo</div>
                      <div className="info-value">
                        {selectedVenda.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Documento</div>
                      <div className="info-value">{formatDocumento(selectedVenda.documento)}</div>
                    </div>
                    {selectedVenda.cpf && (
                      <div className="info-item">
                        <div className="info-label">CPF</div>
                        <div className="info-value">{formatDocumento(selectedVenda.cpf)}</div>
                      </div>
                    )}
                    {selectedVenda.rg && (
                      <div className="info-item">
                        <div className="info-label">RG</div>
                        <div className="info-value">{selectedVenda.rg}</div>
                      </div>
                    )}
                    {selectedVenda.nome_mae && (
                      <div className="info-item">
                        <div className="info-label">Nome da Mãe</div>
                        <div className="info-value">{selectedVenda.nome_mae}</div>
                      </div>
                    )}
                    {selectedVenda.data_nascimento && (
                      <div className="info-item">
                        <div className="info-label">Data de Nascimento</div>
                        <div className="info-value">{formatDateOnly(selectedVenda.data_nascimento)}</div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Telefone</div>
                      <div className="info-value">{formatPhone(selectedVenda.telefone)}</div>
                    </div>
                    {selectedVenda.telefone2 && (
                      <div className="info-item">
                        <div className="info-label">Telefone 2</div>
                        <div className="info-value">{formatPhone(selectedVenda.telefone2)}</div>
                      </div>
                    )}
                    {selectedVenda.email && (
                      <div className="info-item">
                        <div className="info-label">Email</div>
                        <div className="info-value">{selectedVenda.email}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Endereço */}
                <div className="info-section">
                  <h4>📍 Endereço</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">CEP</div>
                      <div className="info-value">{selectedVenda.cep}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Logradouro</div>
                      <div className="info-value">{selectedVenda.logradouro}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Número</div>
                      <div className="info-value">{selectedVenda.numero}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Bairro</div>
                      <div className="info-value">{selectedVenda.bairro}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Cidade</div>
                      <div className="info-value">{selectedVenda.cidade}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Estado</div>
                      <div className="info-value">
                        {selectedVenda.estado_completo} ({selectedVenda.estado})
                      </div>
                    </div>
                    {selectedVenda.complemento1 && (
                      <div className="info-item">
                        <div className="info-label">Complemento 1</div>
                        <div className="info-value">{selectedVenda.complemento1}</div>
                      </div>
                    )}
                    {selectedVenda.complemento2 && (
                      <div className="info-item">
                        <div className="info-label">Complemento 2</div>
                        <div className="info-value">{selectedVenda.complemento2}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Agendamento */}
                <div className="info-section">
                  <h4>📅 Agendamento de Instalação</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Data Opção 1</div>
                      <div className="info-value">
                        {formatDateOnly(selectedVenda.data_agendamento1)} - 
                        {selectedVenda.periodo_agendamento1?.toUpperCase()}
                      </div>
                    </div>
                    {selectedVenda.data_agendamento2 && (
                      <div className="info-item">
                        <div className="info-label">Data Opção 2</div>
                        <div className="info-value">
                          {formatDateOnly(selectedVenda.data_agendamento2)} - 
                          {selectedVenda.periodo_agendamento2?.toUpperCase()}
                        </div>
                      </div>
                    )}
                    {selectedVenda.status === 'aguardando_instalacao' && (
                      <div className="info-item">
                        <div className="info-label">Data de Instalação</div>
                        <div className="info-value" style={{ fontWeight: 600, color: 'var(--azul-dvs)' }}>
                          {(selectedVenda.data_instalacao || selectedVenda.data_agendamento1) ? formatDateOnly(selectedVenda.data_instalacao || selectedVenda.data_agendamento1) : '—'}
                        </div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Aceita Antecipação</div>
                      <div className="info-value">
                        {selectedVenda.aceita_antecipacao ? 'Sim' : 'Não'}
                      </div>
                    </div>
                    {selectedVenda.observacao_instalacao && (
                      <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="info-label">Observação sobre Instalação</div>
                        <div className="info-value">{selectedVenda.observacao_instalacao}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financeiro */}
                <div className="info-section">
                  <h4>💰 Informações Financeiras</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Valor Total</div>
                      <div className="info-value" style={{ color: 'var(--azul-dvs)', fontWeight: '600', fontSize: '1.125rem' }}>
                        {(user?.role === 'backoffice' || user?.role === 'supervisor_bko') && selectedVenda.status === 'concluido'
                          ? '—'
                          : `R$ ${parseFloat(selectedVenda.valor_total || 0).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Data de Vencimento</div>
                      <div className="info-value">Dia {selectedVenda.data_vencimento}</div>
                    </div>
                  </div>
                </div>

                {/* Observações */}
                {selectedVenda.observacao_venda && (
                  <div className="info-section">
                    <h4>📝 Observações da Venda</h4>
                    <div className="observacao-venda-value">
                      {selectedVenda.observacao_venda}
                    </div>
                  </div>
                )}

                {/* Histórico de Observações de Tratamento e Troca de Status */}
                {selectedVenda.observacao_tratamento && (
                  <div className="info-section">
                    <h4>📋 Histórico de Tratamento e Mudanças de Status</h4>
                    <div className="historico-tratamento-container">
                      {parseObservacoesTratamento(selectedVenda.observacao_tratamento).map((obs, index) => (
                        <div
                          key={index}
                          className={`observacao-item ${obs.tipo === 'mudanca_status' ? 'mudanca-status' : ''}`}
                        >
                          {obs.tipo === 'mudanca_status' ? (
                            <>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                marginBottom: '0.75rem'
                              }}>
                                <span style={{ 
                                  fontSize: '1.2rem',
                                  filter: 'drop-shadow(0 0 4px rgba(6, 102, 255, 0.5))'
                                }}>🔄</span>
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: 'var(--azul-dvs)',
                                  fontSize: '0.95rem'
                                }}>
                                  Mudança de Status
                                </span>
                                {obs.dataHora && (
                                  <span style={{ 
                                    marginLeft: 'auto',
                                    fontSize: '0.85rem',
                                    color: 'var(--texto-secundario)',
                                    opacity: 0.8
                                  }}>
                                    {obs.dataHora}
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.75rem',
                                flexWrap: 'wrap'
                              }}>
                                <div style={{
                                  padding: '0.4rem 0.8rem',
                                  background: 'rgba(255, 152, 0, 0.2)',
                                  border: '1px solid rgba(255, 152, 0, 0.4)',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  color: '#ff9800'
                                }}>
                                  {obs.statusAnterior}
                                </div>
                                <span style={{ color: 'var(--texto-secundario)' }}>→</span>
                                <div style={{
                                  padding: '0.4rem 0.8rem',
                                  background: 'rgba(6, 102, 255, 0.2)',
                                  border: '1px solid rgba(6, 102, 255, 0.4)',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  color: 'var(--azul-dvs)',
                                  fontWeight: '600'
                                }}>
                                  {obs.statusNovo}
                                </div>
                              </div>
                              {obs.usuario && (
                                <div style={{ 
                                  marginTop: '0.75rem',
                                  fontSize: '0.85rem',
                                  color: 'var(--texto-secundario)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <span>👤</span>
                                  <span>Por: <strong>{obs.usuario}</strong></span>
                                </div>
                              )}
                            </>
                          ) : obs.tipo === 'observacao' ? (
                            <>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                marginBottom: '0.75rem'
                              }}>
                                <span style={{ fontSize: '1.1rem' }}>💬</span>
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: 'var(--texto-primario)',
                                  fontSize: '0.95rem'
                                }}>
                                  Observação
                                </span>
                                {obs.dataHora && (
                                  <span style={{ 
                                    marginLeft: 'auto',
                                    fontSize: '0.85rem',
                                    color: 'var(--texto-secundario)',
                                    opacity: 0.8
                                  }}>
                                    {obs.dataHora}
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                color: 'var(--texto-primario)',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {obs.texto}
                              </div>
                              {obs.usuario && (
                                <div style={{ 
                                  marginTop: '0.75rem',
                                  fontSize: '0.85rem',
                                  color: 'var(--texto-secundario)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <span>👤</span>
                                  <span>Por: <strong>{obs.usuario}</strong></span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ 
                              color: 'var(--texto-primario)',
                              lineHeight: '1.6',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {obs.texto}
                            </div>
                          )}
                        </div>
                      ))}
                      {parseObservacoesTratamento(selectedVenda.observacao_tratamento).length === 0 && (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '2rem',
                          color: 'var(--texto-secundario)',
                          fontStyle: 'italic'
                        }}>
                          Nenhuma observação de tratamento registrada
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="info-section">
                  <h4>ℹ️ Status e Informações</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Status</div>
                      <div className="info-value">
                        {selectedVenda.status === 'aguardando_instalacao' && (
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                            Data instalação: {(selectedVenda.data_instalacao || selectedVenda.data_agendamento1) ? formatDateOnly(selectedVenda.data_instalacao || selectedVenda.data_agendamento1) : '—'}
                          </div>
                        )}
                        <span className={`status-badge ${getStatusClass(selectedVenda.status)}`}>
                          {getStatusText(selectedVenda.status)}
                        </span>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Vendedor</div>
                      <div className="info-value">{selectedVenda.vendedor?.nome || 'N/A'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Cadastrado em</div>
                      <div className="info-value">{formatDateTime(selectedVenda.created_at)}</div>
                    </div>
                    {selectedVenda.tratado_por && (
                      <>
                        <div className="info-item">
                          <div className="info-label">Tratado por</div>
                          <div className="info-value">{selectedVenda.tratador?.nome}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Tratado em</div>
                          <div className="info-value">{formatDateTime(selectedVenda.tratado_em)}</div>
                        </div>
                      </>
                    )}
                    {/* Botão Voltar à Fila - Backoffice quando atribuída a ele; Supervisor BKO nas vendas dos subordinados */}
                    {(user?.role === 'backoffice' && selectedVenda.tratado_por === user.id) ||
                    (user?.role === 'supervisor_bko' && selectedVenda.tratado_por) ? (
                      <div className="info-item" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                        <button
                          onClick={() => handleVoltarFila(selectedVenda)}
                          className="btn-action"
                          style={{ 
                            background: '#ff9800', 
                            color: 'white',
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          🔄 Voltar à Fila de Tratamento
                        </button>
                      </div>
                    ) : null}
                    {(user?.role === 'admin' || user?.role === 'diretor') && (
                      <>
                        <div className="info-item" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                          <button
                            onClick={() => openAtribuicaoBko(selectedVenda)}
                            className="btn-action"
                            style={{ 
                              background: 'var(--azul-dvs)', 
                              color: 'white',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              marginRight: '0.5rem'
                            }}
                          >
                            🔄 {selectedVenda.tratado_por ? 'Trocar/Remover Atribuição de BKO' : 'Atribuir BKO'}
                          </button>
                          <button
                            onClick={() => openAtribuicaoVendedor(selectedVenda)}
                            className="btn-action"
                            style={{ 
                              background: '#4caf50', 
                              color: 'white',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            👤 Trocar Vendedor
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Informações de Viabilidade */}
                {selectedVenda.operadora_selecionada && (
                  <div className="info-section">
                    <h4>📡 Informações de Viabilidade</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <div className="info-label">Operadora Selecionada</div>
                        <div className="info-value" style={{ fontWeight: '600' }}>
                          {selectedVenda.operadora_selecionada.charAt(0).toUpperCase() + selectedVenda.operadora_selecionada.slice(1)}
                        </div>
                      </div>
                      {selectedVenda.operadora_viabilidade_data && (
                        <div className="info-item">
                          <div className="info-label">Data da Consulta</div>
                          <div className="info-value">
                            {formatDateTime(selectedVenda.operadora_viabilidade_data)}
                          </div>
                        </div>
                      )}
                      {selectedVenda.operadora_viabilidade_json && (
                        <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                          <div className="info-label">Status de Viabilidade</div>
                          <div className="info-value">
                            {(() => {
                              try {
                                let viabilidade = selectedVenda.operadora_viabilidade_json;
                                
                                // Se for string, fazer parse
                                if (typeof viabilidade === 'string') {
                                  try {
                                    viabilidade = JSON.parse(viabilidade);
                                    // Se o resultado ainda for string (double-encoded), fazer parse novamente
                                    if (typeof viabilidade === 'string') {
                                      viabilidade = JSON.parse(viabilidade);
                                    }
                                  } catch (parseError) {
                                    console.error('Erro ao fazer parse do JSON:', parseError);
                                    return <span style={{ color: 'var(--error)' }}>Erro ao processar JSON de viabilidade</span>;
                                  }
                                }
                                
                                // Log para debug
                                console.log('📋 Viabilidade JSON parseado:', viabilidade);
                                console.log('📋 Tipo:', typeof viabilidade);
                                console.log('📋 Operadora selecionada:', selectedVenda.operadora_selecionada);
                                
                                // Se viabilidade é null ou undefined
                                if (!viabilidade) {
                                  console.warn('Viabilidade é null ou undefined');
                                  return <span style={{ color: 'var(--texto-secundario)' }}>Dados de viabilidade não disponíveis</span>;
                                }
                                
                                // Verificar se tem operadoras diretamente ou dentro de uma propriedade
                                let operadoras = null;
                                if (viabilidade.operadoras && typeof viabilidade.operadoras === 'object') {
                                  operadoras = viabilidade.operadoras;
                                } else if (viabilidade.data && viabilidade.data.operadoras) {
                                  operadoras = viabilidade.data.operadoras;
                                } else if (typeof viabilidade === 'object' && !Array.isArray(viabilidade)) {
                                  // Verificar se o objeto inteiro tem estrutura de operadoras
                                  const keys = Object.keys(viabilidade);
                                  const hasOperadoraStructure = keys.some(key => {
                                    const val = viabilidade[key];
                                    return val && typeof val === 'object' && ('coverage' in val);
                                  });
                                  if (hasOperadoraStructure) {
                                    operadoras = viabilidade;
                                  }
                                }
                                
                                if (!operadoras || (typeof operadoras === 'object' && Object.keys(operadoras).length === 0)) {
                                  console.warn('Viabilidade sem operadoras válidas:', {
                                    viabilidade,
                                    operadoras,
                                    keys: viabilidade ? Object.keys(viabilidade) : []
                                  });
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                      <span style={{ color: 'var(--texto-secundario)' }}>Dados de viabilidade não contêm operadoras</span>
                                      <details style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                                        <summary style={{ cursor: 'pointer' }}>Ver estrutura do JSON</summary>
                                        <pre style={{ 
                                          marginTop: '0.5rem', 
                                          padding: '0.5rem', 
                                          background: 'var(--cinza-escuro-2)', 
                                          borderRadius: '4px',
                                          overflow: 'auto',
                                          maxHeight: '200px'
                                        }}>
                                          {JSON.stringify(viabilidade, null, 2)}
                                        </pre>
                                      </details>
                                    </div>
                                  );
                                }

                                // Normalizar nome da operadora (case-insensitive, remover espaços extras)
                                const operadoraSelecionadaNormalizada = selectedVenda.operadora_selecionada 
                                  ? selectedVenda.operadora_selecionada.toLowerCase().trim()
                                  : null;

                                // Procurar operadora no JSON (case-insensitive)
                                let operadora = null;
                                if (operadoraSelecionadaNormalizada && operadoras) {
                                  // Tentar encontrar pelo nome exato primeiro
                                  operadora = operadoras[selectedVenda.operadora_selecionada];
                                  
                                  // Se não encontrar, procurar case-insensitive
                                  if (!operadora) {
                                    const operadorasKeys = Object.keys(operadoras);
                                    const operadoraEncontrada = operadorasKeys.find(key => 
                                      key.toLowerCase().trim() === operadoraSelecionadaNormalizada
                                    );
                                    if (operadoraEncontrada) {
                                      operadora = operadoras[operadoraEncontrada];
                                    }
                                  }
                                }

                                if (operadora) {
                                  // Normalizar coverage para boolean
                                  const hasCoverage = operadora.coverage === true || 
                                                     operadora.coverage === 'true' || 
                                                     operadora.coverage === 1 ||
                                                     operadora.coverage === '1';
                                  
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                      <span className={`status-badge ${hasCoverage ? 'status-concluido' : 'status-cancelado'}`} style={{
                                        background: hasCoverage ? '#d4edda' : '#f8d7da',
                                        color: hasCoverage ? '#155724' : '#721c24',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        display: 'inline-block',
                                        fontWeight: '600',
                                        width: 'fit-content'
                                      }}>
                                        {hasCoverage ? '✓ Viabilidade Confirmada' : '⚠ Sem Viabilidade Confirmada'}
                                      </span>
                                      
                                      {/* Mostrar todas as operadoras disponíveis */}
                                      {Object.keys(operadoras).length > 1 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <span style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', fontWeight: '600' }}>
                                            Todas as operadoras consultadas:
                                          </span>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {Object.keys(operadoras).map(nome => {
                                              const op = operadoras[nome];
                                              const opHasCoverage = op && (op.coverage === true || op.coverage === 'true' || op.coverage === 1 || op.coverage === '1');
                                              const isSelected = nome.toLowerCase() === operadoraSelecionadaNormalizada;
                                              return (
                                                <span 
                                                  key={nome} 
                                                  style={{
                                                    padding: '0.4rem 0.75rem',
                                                    background: opHasCoverage ? '#d4edda' : '#f8d7da',
                                                    color: opHasCoverage ? '#155724' : '#721c24',
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: isSelected ? '600' : '400',
                                                    border: isSelected ? '2px solid var(--azul-dvs)' : '1px solid transparent'
                                                  }}
                                                >
                                                  {nome.charAt(0).toUpperCase() + nome.slice(1)}: {opHasCoverage ? '✓' : '✗'}
                                                  {isSelected && ' (Selecionada)'}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                
                                // Se não encontrou a operadora específica, mostrar todas as operadoras disponíveis
                                const operadorasDisponiveis = Object.keys(operadoras || {});
                                if (operadorasDisponiveis.length > 0) {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                      {selectedVenda.operadora_selecionada && (
                                        <span style={{ color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                                          Operadora "{selectedVenda.operadora_selecionada}" não encontrada no JSON.
                                        </span>
                                      )}
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {operadorasDisponiveis.map(nome => {
                                          const op = operadoras[nome];
                                          const hasCoverage = op && (op.coverage === true || op.coverage === 'true' || op.coverage === 1 || op.coverage === '1');
                                          return (
                                            <span key={nome} style={{
                                              padding: '0.25rem 0.5rem',
                                              background: hasCoverage ? '#d4edda' : '#f8d7da',
                                              color: hasCoverage ? '#155724' : '#721c24',
                                              borderRadius: '4px',
                                              fontSize: '0.85rem'
                                            }}>
                                              {nome}: {hasCoverage ? '✓' : '✗'}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return <span style={{ color: 'var(--texto-secundario)' }}>Nenhuma operadora encontrada no JSON</span>;
                              } catch (e) {
                                console.error('Erro ao processar viabilidade:', e);
                                return <span style={{ color: 'var(--error)' }}>Erro ao processar dados de viabilidade</span>;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {histVizOpen && (
              <div
                className="modal-overlay"
                style={{ zIndex: 10050, position: 'fixed', inset: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setHistVizOpen(false);
                }}
              >
                <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px', maxHeight: '85vh', overflow: 'auto' }}>
                  <div className="modal-header-venda">
                    <h2>Histórico de visualização — Venda #{selectedVenda.id}</h2>
                    <span className="close-modal" onClick={() => setHistVizOpen(false)}>×</span>
                  </div>
                  <div className="modal-body-venda">
                    {histVizLoading ? (
                      <p>Carregando…</p>
                    ) : histVizItems.length === 0 ? (
                      <p style={{ color: 'var(--texto-secundario)' }}>Nenhum registro.</p>
                    ) : (
                      <div className="vendas-table" style={{ display: 'block' }}>
                        <div className="table-header" style={{ gridTemplateColumns: '1fr 1fr 1fr', fontSize: '0.85rem' }}>
                          <div>Usuário</div>
                          <div>Data/hora</div>
                          <div>Origem</div>
                        </div>
                        {histVizItems.map((row) => (
                          <div key={row.id} className="table-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', fontSize: '0.85rem' }}>
                            <div>{row.usuario?.nome || row.usuario?.username || `#${row.user_id}`}</div>
                            <div>{formatDateTime(row.viewed_at)}</div>
                            <div>{ORIGEM_VISUALIZACAO_LABEL[row.source_page] || row.source_page}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal de Editar Venda */}
        {editingVenda && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '95vh' }}>
              <div className="modal-header-venda">
                <h2>✏️ Editar Venda #{editingVenda.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <form onSubmit={handleEditSubmit} className="modal-body-venda" style={{ maxHeight: 'calc(95vh - 100px)', overflowY: 'auto' }}>
                {/* Status */}
                <div className="info-section">
                  <h4>📊 Status da Venda</h4>
                  {['admin', 'diretor'].includes(user?.role) && (
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Status *</label>
                      <select
                        value={editingVenda.status || 'pendente_bko'}
                        onChange={(e) => setEditingVenda({ ...editingVenda, status: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      >
                        <option value="pendente_bko">Pendente BKO</option>
                        <option value="pendente_vendedor">Pendente Vendedor</option>
                        <option value="pendente_sistemica">Pendente Sistêmica</option>
                        <option value="aguardando_instalacao">Aguardando Instalação</option>
                        <option value="concluido">Concluído</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </div>
                  )}
                  {user?.role === 'vendedor' && editingVenda.status === 'pendente_vendedor' && (
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Status *</label>
                      <select
                        value={editingVenda.status}
                        onChange={(e) => setEditingVenda({ ...editingVenda, status: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      >
                        <option value="pendente_vendedor">Pendente Vendedor</option>
                        <option value="pendente_bko">Pendente BKO</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Tipo e Documento */}
                <div className="info-section">
                  <h4>📋 Tipo de Cliente</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Tipo *</div>
                      <select
                        value={editingVenda.tipo_cliente || 'cpf'}
                        onChange={(e) => setEditingVenda({ ...editingVenda, tipo_cliente: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      >
                        <option value="cpf">CPF</option>
                        <option value="cnpj">CNPJ</option>
                      </select>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Documento *</div>
                      <input
                        type="text"
                        value={editingVenda.documento ? (editingVenda.tipo_cliente === 'cpf' ? maskCPF(editingVenda.documento) : maskCNPJ(editingVenda.documento)) : ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, documento: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Informações do Cliente */}
                <div className="info-section">
                  <h4>👤 Informações do Cliente</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">{editingVenda.tipo_cliente === 'cnpj' ? 'Razão Social *' : 'Nome Completo *'}</div>
                      <input
                        type="text"
                        value={editingVenda.razao_social || editingVenda.nome_cliente || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, [editingVenda.tipo_cliente === 'cnpj' ? 'razao_social' : 'nome_cliente']: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    {editingVenda.tipo_cliente === 'cnpj' && (
                      <div className="info-item">
                        <div className="info-label">Nome do Cliente</div>
                        <input
                          type="text"
                          value={editingVenda.nome_cliente || ''}
                          onChange={(e) => setEditingVenda({ ...editingVenda, nome_cliente: e.target.value })}
                          style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                        />
                      </div>
                    )}
                    {editingVenda.tipo_cliente === 'cpf' && (
                      <>
                        <div className="info-item">
                          <div className="info-label">CPF</div>
                          <input
                            type="text"
                            value={editingVenda.cpf ? maskCPF(editingVenda.cpf) : ''}
                            onChange={(e) => setEditingVenda({ ...editingVenda, cpf: e.target.value })}
                            style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                          />
                        </div>
                        <div className="info-item">
                          <div className="info-label">RG</div>
                          <input
                            type="text"
                            value={editingVenda.rg || ''}
                            onChange={(e) => setEditingVenda({ ...editingVenda, rg: e.target.value })}
                            style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                          />
                        </div>
                        <div className="info-item">
                          <div className="info-label">Nome da Mãe</div>
                          <input
                            type="text"
                            value={editingVenda.nome_mae || ''}
                            onChange={(e) => setEditingVenda({ ...editingVenda, nome_mae: e.target.value })}
                            style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                          />
                        </div>
                        <div className="info-item">
                          <div className="info-label">Data de Nascimento</div>
                          <input
                            type="date"
                            value={editingVenda.data_nascimento || ''}
                            onChange={(e) => setEditingVenda({ ...editingVenda, data_nascimento: e.target.value })}
                            style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                          />
                        </div>
                      </>
                    )}
                    {editingVenda.tipo_cliente === 'cnpj' && (
                      <div className="info-item">
                        <div className="info-label">Estado do CNPJ</div>
                        <input
                          type="text"
                          value={editingVenda.estado_cnpj || ''}
                          onChange={(e) => setEditingVenda({ ...editingVenda, estado_cnpj: e.target.value })}
                          maxLength={2}
                          style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                        />
                      </div>
                    )}
                    {editingVenda.tipo_cliente === 'cnpj' && (
                      <div className="info-item">
                        <div className="info-label">Nome do Representante</div>
                        <input
                          type="text"
                          value={editingVenda.nome_representante || ''}
                          onChange={(e) => setEditingVenda({ ...editingVenda, nome_representante: e.target.value })}
                          placeholder="Nome completo do representante legal"
                          style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                        />
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Telefone 1 *</div>
                      <input
                        type="text"
                        value={editingVenda.telefone ? maskPhone(editingVenda.telefone) : ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, telefone: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Telefone 2</div>
                      <input
                        type="text"
                        value={editingVenda.telefone2 ? maskPhone(editingVenda.telefone2) : ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, telefone2: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Email</div>
                      <input
                        type="email"
                        value={editingVenda.email || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, email: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="info-section">
                  <h4>📍 Endereço</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">CEP *</div>
                      <input
                        type="text"
                        value={editingVenda.cep ? maskCEP(editingVenda.cep) : ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, cep: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Logradouro *</div>
                      <input
                        type="text"
                        value={editingVenda.logradouro || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, logradouro: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Número *</div>
                      <input
                        type="text"
                        value={editingVenda.numero || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, numero: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Bairro *</div>
                      <input
                        type="text"
                        value={editingVenda.bairro || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, bairro: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Cidade *</div>
                      <input
                        type="text"
                        value={editingVenda.cidade || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, cidade: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Estado *</div>
                      <input
                        type="text"
                        value={editingVenda.estado || ''}
                        maxLength={2}
                        onChange={(e) => setEditingVenda({ ...editingVenda, estado: e.target.value.toUpperCase() })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Estado Completo *</div>
                      <input
                        type="text"
                        value={editingVenda.estado_completo || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, estado_completo: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Complemento 1</div>
                      <input
                        type="text"
                        value={editingVenda.complemento1 || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, complemento1: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Complemento 2</div>
                      <input
                        type="text"
                        value={editingVenda.complemento2 || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, complemento2: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Agendamento */}
                <div className="info-section">
                  <h4>📅 Agendamento de Instalação</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Data Opção 1 *</div>
                      <input
                        type="date"
                        value={editingVenda.data_agendamento1 || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, data_agendamento1: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Período Opção 1 *</div>
                      <select
                        value={editingVenda.periodo_agendamento1 || 'manha'}
                        onChange={(e) => setEditingVenda({ ...editingVenda, periodo_agendamento1: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      >
                        <option value="manha">Manhã</option>
                        <option value="tarde">Tarde</option>
                        <option value="noite">Noite</option>
                      </select>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Data Opção 2</div>
                      <input
                        type="date"
                        value={editingVenda.data_agendamento2 || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, data_agendamento2: e.target.value || null })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="info-item">
                      <div className="info-label">Período Opção 2</div>
                      <select
                        value={editingVenda.periodo_agendamento2 || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, periodo_agendamento2: e.target.value || null })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      >
                        <option value="">Selecione...</option>
                        <option value="manha">Manhã</option>
                        <option value="tarde">Tarde</option>
                        <option value="noite">Noite</option>
                      </select>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Aceita Antecipação</div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editingVenda.aceita_antecipacao || false}
                          onChange={(e) => setEditingVenda({ ...editingVenda, aceita_antecipacao: e.target.checked })}
                          style={{ width: 'auto' }}
                        />
                        <span>Sim</span>
                      </label>
                    </div>
                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                      <div className="info-label">Observação sobre Instalação</div>
                      <textarea
                        value={editingVenda.observacao_instalacao || ''}
                        onChange={(e) => setEditingVenda({ ...editingVenda, observacao_instalacao: e.target.value })}
                        rows="3"
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)', fontFamily: 'inherit' }}
                        placeholder="Observações sobre a instalação..."
                      />
                    </div>
                  </div>
                </div>

                {/* Financeiro */}
                <div className="info-section">
                  <h4>💰 Informações Financeiras</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Valor Total (R$) *</div>
                      {(user?.role === 'backoffice' || user?.role === 'supervisor_bko') && editingVenda.status === 'concluido'
                        ? <div className="info-value" style={{ padding: '0.75rem', color: 'var(--texto-secundario)' }}>—</div>
                        : (
                          <input
                            type="text"
                            value={editingVenda.valor_total ? (typeof editingVenda.valor_total === 'number' ? maskMoney((editingVenda.valor_total * 100).toString()) : editingVenda.valor_total) : ''}
                            onChange={(e) => setEditingVenda({ ...editingVenda, valor_total: e.target.value })}
                            style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                          />
                        )}
                    </div>
                    <div className="info-item">
                      <div className="info-label">Data de Vencimento *</div>
                      <select
                        value={editingVenda.data_vencimento || 10}
                        onChange={(e) => setEditingVenda({ ...editingVenda, data_vencimento: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                      >
                        <option value={1}>Dia 1</option>
                        <option value={10}>Dia 10</option>
                        <option value={17}>Dia 17</option>
                        <option value={21}>Dia 21</option>
                        <option value={26}>Dia 26</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Observações */}
                <div className="info-section">
                  <h4>📝 Observações</h4>
                  <div className="form-group">
                    <label>Observação da Venda</label>
                    <textarea
                      value={editingVenda.observacao_venda || ''}
                      onChange={(e) => setEditingVenda({ ...editingVenda, observacao_venda: e.target.value })}
                      rows="4"
                      style={{ width: '100%', padding: '0.75rem', background: '#2a2a2a', border: '1px solid var(--border-color)', borderRadius: '6px', color: '#ffffff', fontFamily: 'inherit' }}
                      placeholder="Observações gerais sobre a venda..."
                    />
                  </div>
                </div>

                {/* Operadora */}
                {(editingVenda.operadora_selecionada || editingVenda.operadora_viabilidade_json) && (
                  <div className="info-section">
                    <h4>📡 Informações de Viabilidade</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <div className="info-label">Operadora Selecionada</div>
                        <input
                          type="text"
                          value={editingVenda.operadora_selecionada || ''}
                          onChange={(e) => setEditingVenda({ ...editingVenda, operadora_selecionada: e.target.value })}
                          style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--texto-primario)' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    ✅ Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Mudar Status */}
        {changingStatus && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>🔄 Alterar Status - Venda #{changingStatus.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <div className="info-section">
                  <div className="info-item">
                    <div className="info-label">Status Atual</div>
                    <div className="info-value">
                      <span className={`status-badge ${getStatusClass(changingStatus.status)}`}>
                        {getStatusText(changingStatus.status)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="form-group" style={{ marginTop: '1.5rem' }}>
                    <label>Novo Status *</label>
                    <select
                      value={newStatus}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewStatus(val);
                        if (val === 'aguardando_instalacao') {
                          setNewDataInstalacao(changingStatus?.data_instalacao || changingStatus?.data_agendamento1 || getTodayBrasilia());
                        } else {
                          setNewDataInstalacao('');
                        }
                      }}
                      style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                    >
                      <option value="">Selecione o status</option>
                      <option value="pendente_bko">Pendente BKO</option>
                      <option value="tratando">Tratando</option>
                      <option value="pendente_vendedor">Pendente Vendedor</option>
                      <option value="pendente_sistemica">Pendente Sistêmica</option>
                      <option value="pendente_biometria">Pendente Biometria</option>
                      <option value="pendente_auditoria">Pendente Auditoria</option>
                      <option value="pendente_imput">Pendente Imput</option>
                      <option value="aguardando_instalacao">Aguardando Instalação</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>

                  {newStatus === 'aguardando_instalacao' && (
                    <div className="form-group" style={{ marginTop: '1rem' }}>
                      <label>Data da Instalação *</label>
                      <input
                        type="date"
                        value={newDataInstalacao || ''}
                        onChange={(e) => setNewDataInstalacao(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      />
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                        Data em que está agendada a instalação (usada no filtro da página Vendas).
                      </p>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleChangeStatus}
                    className="btn-primary"
                    disabled={!newStatus || (newStatus === 'aguardando_instalacao' && !newDataInstalacao?.trim())}
                  >
                    ✅ Alterar Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Atribuição de BKO */}
        {changingAtribuicao && (user?.role === 'admin' || user?.role === 'diretor') && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>🔄 Atribuir/Trocar BKO - Venda #{changingAtribuicao.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <form onSubmit={handleAtribuicaoBkoSubmit}>
                  <div className="info-section">
                    <div className="info-item">
                      <div className="info-label">BKO Atual</div>
                      <div className="info-value">
                        {changingAtribuicao.tratador?.nome || 'Não atribuído'}
                      </div>
                    </div>
                    
                    <div className="form-group" style={{ marginTop: '1.5rem' }}>
                      <label>Atribuir para BKO:</label>
                      <select
                        value={novoTratadoPor}
                        onChange={(e) => setNovoTratadoPor(e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          background: 'var(--cinza-escuro-2)', 
                          border: '1px solid var(--border-color)', 
                          borderRadius: '8px', 
                          color: 'var(--texto-primario)' 
                        }}
                      >
                        <option value="">Limpar atribuição (Não atribuído)</option>
                        {backoffices.map(bko => (
                          <option key={bko.id} value={bko.id}>
                            {bko.nome} ({bko.username})
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
                        Selecione um BKO para atribuir ou deixe vazio para remover a atribuição.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                    <button type="button" onClick={closeModal} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary">
                      ✅ {(!novoTratadoPor || novoTratadoPor === '' || novoTratadoPor.toString().trim() === '') 
                        ? 'Remover Atribuição' 
                        : 'Atualizar Atribuição'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Troca de Vendedor */}
        {changingVendedor && (user?.role === 'admin' || user?.role === 'diretor') && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>👤 Trocar Vendedor - Venda #{changingVendedor.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <form onSubmit={handleAtribuicaoVendedorSubmit}>
                  <div className="info-section">
                    <div className="info-item">
                      <div className="info-label">Vendedor Atual</div>
                      <div className="info-value">
                        {changingVendedor.vendedor?.nome || 'Não atribuído'}
                      </div>
                    </div>
                    
                    <div className="form-group" style={{ marginTop: '1.5rem' }}>
                      <label>Selecione o novo vendedor: *</label>
                      <select
                        value={novoVendedorId}
                        onChange={(e) => setNovoVendedorId(e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          background: 'var(--cinza-escuro-2)', 
                          border: '1px solid var(--border-color)', 
                          borderRadius: '8px', 
                          color: 'var(--texto-primario)' 
                        }}
                        required
                      >
                        <option value="">Selecione um vendedor</option>
                        {vendedores.map(vendedor => (
                          <option key={vendedor.id} value={vendedor.id}>
                            {vendedor.nome} ({vendedor.username}) - {vendedor.role}
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
                        Selecione o vendedor responsável por esta venda.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                    <button type="button" onClick={closeModal} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={!novoVendedorId}>
                      ✅ Atualizar Vendedor
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Escolher Período para Exportar Vendas */}
        {showModalExportar && (user?.role === 'admin' || user?.role === 'diretor') && (
          <div className="modal-overlay" onClick={() => setShowModalExportar(false)}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>📥 Escolha o período para exportação</h2>
                <span className="close-modal" onClick={() => setShowModalExportar(false)}>×</span>
              </div>
              <div className="modal-body-venda">
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.75rem' }}>Período</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" name="periodoExportar" value="hoje" checked={periodoExportar === 'hoje'} onChange={(e) => setPeriodoExportar(e.target.value)} />
                      Hoje
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" name="periodoExportar" value="semana" checked={periodoExportar === 'semana'} onChange={(e) => setPeriodoExportar(e.target.value)} />
                      Semana (segunda a domingo)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" name="periodoExportar" value="mes" checked={periodoExportar === 'mes'} onChange={(e) => setPeriodoExportar(e.target.value)} />
                      Mês (dia 01 ao último dia)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" name="periodoExportar" value="personalizado" checked={periodoExportar === 'personalizado'} onChange={(e) => setPeriodoExportar(e.target.value)} />
                      Personalizado
                    </label>
                  </div>
                </div>
                {periodoExportar === 'personalizado' && (
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                      <label>Data inicial</label>
                      <input
                        type="date"
                        value={dataInicioExportar}
                        onChange={(e) => setDataInicioExportar(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                      <label>Data final</label>
                      <input
                        type="date"
                        value={dataFimExportar}
                        onChange={(e) => setDataFimExportar(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={() => setShowModalExportar(false)} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="button" onClick={handleConfirmarExportar} className="btn-primary">
                    📥 Exportar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Vendas;
